import { CheckBoxField, TextArea } from 'components/core';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import { currentVisualizationState } from 'states';

import { CONFIG_FIELD_DOCS } from 'constants/analyticsConfigDocs';
import { CHART_TYPE, UI_TEXT } from 'constants/appConstants';
import styles from './VisualizationTabs.module.css';
import { FieldTooltip } from 'molecules/fieldTooltip/FieldTooltip';
import { CONFIG_TEMPLATES, DEFAULT_CONFIG_TEMPLATE } from 'constants/analyticsTemplateDocs';

interface ConfigProps {
  register: any;
  setValue: any;
  formState: any;
  watch: any;
  isExisting?: boolean;
  chartType?: string;
}

const handleConfigChange = (setValue: any) => (e: any) => {
  setValue('config', e.target.value, { shouldValidate: true });
};

// Parses the config textarea's current raw string for the search-toggle
// checkbox's checked state. The textarea itself stays the source of truth —
// this never writes back on its own, so a momentarily-invalid JSON edit in
// progress just falls back to "unchecked" rather than throwing.
const parseConfigSafely = (raw: string): Record<string, any> => {
  try {
    return JSON.parse(raw) ?? {};
  } catch {
    return {};
  }
};

export const Config = ({
  register,
  setValue,
  formState,
  watch,
  isExisting = false,
  chartType = '',
}: ConfigProps) => {
  const { t } = useTranslation();
  const currentVisualization = useRecoilValue(currentVisualizationState);

  // ── Load config template based on selected chart type ─────────────────────
  useEffect(() => {
    if (!isExisting) {
      const template = CONFIG_TEMPLATES[chartType?.toLowerCase()] ?? DEFAULT_CONFIG_TEMPLATE;
      setValue('config', template);
    }
  }, [currentVisualization, chartType]);

  const { errors } = formState;

  const chartTypeLabel = chartType
    ? chartType.charAt(0).toUpperCase() + chartType.slice(1).toLowerCase()
    : null;

  // ── Chart-search toggle — stacked bar only (the chart type this was built
  // for: KPI-010's ~600-category chart). Reads/writes straight into the
  // config JSON string rather than its own RHF field, since it's a single
  // boolean riding inside the existing textarea, not a separate document
  // like the Filters tab's kpiFilters.
  const isStackedBar = chartType?.toLowerCase() === CHART_TYPE.STACKBAR;
  const configValue = watch ? watch('config') : undefined;
  const parsedConfig = parseConfigSafely(configValue ?? '');
  const isChartSearchEnabled = !!parsedConfig.enableChartSearch;

  const toggleChartSearch = useCallback(() => {
    const next = { ...parsedConfig, enableChartSearch: !isChartSearchEnabled };
    setValue('config', JSON.stringify(next, null, 2), { shouldValidate: true });
  }, [parsedConfig, isChartSearchEnabled, setValue]);

  return (
    <div style={{ paddingBottom: '40px' }} className="col-sm-12 col-md-12 col-lg-8 mt-4 mb-3">

      {/* ── Field reference panel ─────────────────────────────────────────── */}
      <div className={styles.fieldRefPanel}>
        <div className={styles.fieldRefHeader}>
          <span className="material-icons" style={{ fontSize: 15, color: 'var(--primary-1)' }}>
            menu_book
          </span>
          <span className={styles.fieldRefTitle}>{UI_TEXT.VISUALIZATION.CONFIG_REF_TITLE}</span>
          <span className={styles.fieldRefSub}>{UI_TEXT.VISUALIZATION.CONFIG_REF_SUB}</span>
        </div>
        <div className={styles.fieldRefGrid}>
          {CONFIG_FIELD_DOCS.map((doc) => (
            <div key={doc.field} className={styles.fieldRefItem}>
              <div className={styles.fieldRefItemTop}>
                <code className={styles.fieldRefCode}>{doc.label}</code>
                <FieldTooltip
                  title={doc.label}
                  description={doc.description}
                  example={doc.example}
                  note={doc.note}
                />
              </div>
              {doc.values && (
                <div className={styles.fieldRefValues}>
                  <span className={styles.fieldRefValuesLabel}>Valid values: </span>
                  {doc.values}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* ── Config editor ─────────────────────────────────────────────────── */}
      <div className="mb-4">
        <div className={styles.fieldLabelRow}>
          <span className={styles.fieldLabel}>
            {t('VISUALIZATION.FIELD_CONFIG')} <span className={styles.required}>*</span>
          </span>
          <FieldTooltip
            title={UI_TEXT.VISUALIZATION.CONFIG_TOOLTIP_TITLE}
            description={UI_TEXT.VISUALIZATION.CONFIG_TOOLTIP_DESC}
            example={UI_TEXT.VISUALIZATION.CONFIG_TOOLTIP_EXAMPLE}
            note={UI_TEXT.VISUALIZATION.CONFIG_TOOLTIP_NOTE}
          />
        </div>

        {/* Validation error banner — shown above editor when config fails */}
        {errors.config && (
          <div className={styles.configErrorBanner}>
            <span className="material-icons">error</span>
            <div>
              <strong>{UI_TEXT.VISUALIZATION.CONFIG_ERROR_TITLE}</strong>
              <p style={{ margin: '4px 0 0', fontSize: 12 }}>
                {UI_TEXT.VISUALIZATION.CONFIG_ERROR_BODY}
              </p>
            </div>
          </div>
        )}

        {/* Starter template info note — only for new visualizations */}
        {!isExisting && (
          <div className={styles.jsonNote}>
            <span className="material-icons" style={{ fontSize: 16, flexShrink: 0 }}>info</span>
            <span>
              <strong>New visualization</strong>
              {chartTypeLabel
                ? ` — ${UI_TEXT.VISUALIZATION.TEMPLATE_NOTE_CHART} `
                : ` — ${UI_TEXT.VISUALIZATION.TEMPLATE_NOTE_PREFIX}. `}
              {chartTypeLabel && <strong>{chartTypeLabel}</strong>}
              {'. '}
              {UI_TEXT.VISUALIZATION.TEMPLATE_NOTE_CONFIG}
            </span>
          </div>
        )}

        <TextArea
          description={UI_TEXT.VISUALIZATION.CONFIG_TEXTAREA_DESC}
          descriptionPos="top"
          showDescription={true}
          rows={12}
          showTitle={false}
          title=""
          {...register('config')}
          className={`${errors.config ? 'is-invalid' : ''}`}
          onChange={handleConfigChange(setValue)}
        />
        <div className="invalid-feedback">{errors.config?.message}</div>

        {/* Chart-search toggle — stacked bar only, sets enableChartSearch in
            the config JSON above. Shows a search box on the live chart for
            filtering categories client-side (built for KPI-010's ~600
            course/level categories, applies to any large-category stacked
            bar). */}
        {isStackedBar && (
          <div className="mt-3">
            <CheckBoxField
              label="Enable chart search"
              subLabel="Adds a search box above this chart so viewers can filter to a specific category without a new query. Stacked bar charts only."
              isChecked={isChartSearchEnabled}
              clickHandler={toggleChartSearch}
            />
          </div>
        )}
      </div>
    </div>
  );
};